import React, { useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet-async";
import { COUNTRIES, getUniversitiesByCountry } from "../data/index";
import "./StudyDestination.css";

gsap.registerPlugin(ScrollTrigger);

export default function StudyDestination() {
  const { country } = useParams(); // e.g. "germany", "uk", "canada", "australia"
  const navigate = useNavigate();
  const rootRef = useRef(null);

  // Normalise slug — just in case the URL has uppercase or trailing slash
  const slug = country?.toLowerCase().trim();
  const data = COUNTRIES[slug];

  // Only redirect if slug is genuinely not found — never redirect when data exists
  useEffect(() => {
    if (slug && !data) {
      console.warn(`[StudyDestination] No data found for slug: "${slug}". Available:`, Object.keys(COUNTRIES));
      navigate("/", { replace: true });
    }
  }, [slug, data, navigate]);

  useEffect(() => {
    if (!data) return;
    const ctx = gsap.context(() => {
      // Hero reveal
      gsap.fromTo(".sd-hero-badge", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, delay: 0.1, ease: "power3.out" });
      gsap.fromTo(".sd-hero-h1 .line-inner", { y: "110%" }, { y: 0, duration: 1, delay: 0.2, stagger: 0.1, ease: "power4.out" });
      gsap.fromTo(".sd-hero-sub", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.5, ease: "power3.out" });
      gsap.fromTo(".sd-hero-stats", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.65, ease: "power3.out" });
      gsap.fromTo(".sd-hero-actions", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, delay: 0.8, ease: "power3.out" });
      gsap.fromTo(".sd-hero-img-wrap", { opacity: 0, scale: 1.04 }, { opacity: 1, scale: 1, duration: 1.2, delay: 0.1, ease: "power3.out" });

      // Scroll reveals
      const sections = [".sd-overview", ".sd-why", ".sd-process-section", ".sd-unis", ".sd-cta"];
      sections.forEach((sel) => {
        gsap.fromTo(sel,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0, duration: 0.85, ease: "power3.out",
            scrollTrigger: { trigger: sel, start: "top 82%", once: true }
          }
        );
      });

      gsap.fromTo(".sd-why-card",
        { opacity: 0, y: 32 },
        {
          opacity: 1, y: 0, duration: 0.65, stagger: 0.08, ease: "power3.out",
          scrollTrigger: { trigger: ".sd-why", start: "top 78%", once: true }
        }
      );

      gsap.fromTo(".sd-proc-step",
        { opacity: 0, x: -24 },
        {
          opacity: 1, x: 0, duration: 0.65, stagger: 0.1, ease: "power3.out",
          scrollTrigger: { trigger: ".sd-process-section", start: "top 78%", once: true }
        }
      );

      gsap.fromTo(".sd-uni-card",
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: "power3.out",
          scrollTrigger: { trigger: ".sd-unis", start: "top 80%", once: true }
        }
      );
    }, rootRef);

    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, [data]);

  // While slug is resolving or data missing — render nothing (redirect useEffect handles invalid slugs)
  if (!data) return null;

  const universities = getUniversitiesByCountry(slug);

  return (
    <div className="sd" ref={rootRef}>
      <Helmet>
        <title>Study in {data.name} | Courses, Universities & Visa | ISTP Overseas</title>
        <meta name="description" content={`Planning to study in ${data.name}? ISTP Overseas guides you through top universities, tuition fees, IELTS requirements, visa process and scholarships. ${data.students} students placed.`} />
        <link rel="canonical" href={`https://www.istpoverseas.com/study-in/${slug}`} />
        <meta property="og:title" content={`Study in ${data.name} | ISTP Overseas`} />
        <meta property="og:description" content={`Top universities, visa guidance and scholarships for studying in ${data.name}.`} />
        <meta property="og:url" content={`https://www.istpoverseas.com/study-in/${slug}`} />
      </Helmet>
      {/* ── Hero ── */}
      <section className="sd-hero">
        <div className="sd-hero-img-wrap">
          <img src={data.heroImg} alt={data.name} />
          <div className="sd-hero-overlay" />
        </div>

        <div className="sd-hero-content">
          <div className="sd-hero-inner">
            <span className="sd-hero-badge">
              {data.flag} Study in {data.name}
            </span>
            <h1 className="sd-hero-h1">
              <span className="line-mask"><span className="line-inner">{data.tagline.split(" ").slice(0, 3).join(" ")}</span></span>
              <span className="line-mask"><span className="line-inner">{data.tagline.split(" ").slice(3).join(" ")}</span></span>
            </h1>
            <p className="sd-hero-sub">{data.overview.substring(0, 160)}…</p>

            <div className="sd-hero-stats">
              {data.stats.map((s, i) => (
                <div key={i} className="sd-hero-stat">
                  <span className="sd-hero-stat-n">{s.n}</span>
                  <span className="sd-hero-stat-l">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="sd-hero-actions">
              <Link to="/book-slot" className="sd-btn-primary">Book Free Consultation</Link>
              <Link to="/student-meets" className="sd-btn-outline">Join Student Meet →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick facts bar ── */}
      <section className="sd-facts-bar">
        {[
          { label: "Avg. Tuition", value: data.avgTuition },
          { label: "Avg. Living Cost", value: data.avgLiving },
          { label: "Post-Study Work", value: data.workPermit },
          { label: "IELTS Requirement", value: data.ieltsReq },
          { label: "Main Intakes", value: data.intakeMonths.join(" · ") },
        ].map((f, i) => (
          <div key={i} className="sd-fact-item">
            <span className="sd-fact-label">{f.label}</span>
            <span className="sd-fact-value">{f.value}</span>
          </div>
        ))}
      </section>

      {/* ── Overview ── */}
      <section className="sd-overview">
        <div className="sd-container">
          <div className="sd-overview-inner">
            <div className="sd-overview-text">
              <span className="sd-eyebrow">Overview</span>
              <h2 className="sd-section-h2">Why {data.name}?</h2>
              <p className="sd-overview-para">{data.overview}</p>
              <Link to="/book-slot" className="sd-link-arrow">Get personalised guidance →</Link>
            </div>
            <div className="sd-overview-card">
              <div className="sd-oc-flag">{data.flag}</div>
              <h3 className="sd-oc-title">{data.name} at a glance</h3>
              <div className="sd-oc-rows">
                <div className="sd-oc-row"><span>Tuition</span><strong>{data.avgTuition}</strong></div>
                <div className="sd-oc-row"><span>Living Cost</span><strong>{data.avgLiving}</strong></div>
                <div className="sd-oc-row"><span>Post-Study Work</span><strong>{data.workPermit}</strong></div>
                <div className="sd-oc-row"><span>IELTS</span><strong>{data.ieltsReq}</strong></div>
                <div className="sd-oc-row"><span>Intakes</span><strong>{data.intakeMonths.join(", ")}</strong></div>
                <div className="sd-oc-row"><span>ISTP Students Placed</span><strong>{data.students}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Choose ── */}
      <section className="sd-why">
        <div className="sd-container">
          <div className="sd-section-head">
            <span className="sd-eyebrow sd-eyebrow--center">Reasons to Choose</span>
            <h2 className="sd-section-h2 sd-section-h2--center">
              Why students choose {data.flag} {data.name}
            </h2>
          </div>
          <div className="sd-why-grid">
            {data.whyChoose.map((item, i) => (
              <div key={i} className="sd-why-card">
                <span className="sd-why-icon">{item.icon}</span>
                <h3 className="sd-why-title">{item.title}</h3>
                <p className="sd-why-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="sd-process-section">
        <div className="sd-container">
          <div className="sd-section-head">
            <span className="sd-eyebrow sd-eyebrow--center">How It Works</span>
            <h2 className="sd-section-h2 sd-section-h2--center">
              Your path to {data.name}, step by step
            </h2>
          </div>
          <div className="sd-process-track">
            {data.process.map((p, i) => (
              <div key={i} className="sd-proc-step">
                <div className="sd-proc-num">{p.step}</div>
                <div className="sd-proc-connector" />
                <h3 className="sd-proc-title">{p.title}</h3>
                <p className="sd-proc-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Universities ── */}
      <section className="sd-unis">
        <div className="sd-container">
          <div className="sd-section-head">
            <span className="sd-eyebrow sd-eyebrow--center">Partner Institutions</span>
            <h2 className="sd-section-h2 sd-section-h2--center">
              Top universities in {data.name} we work with
            </h2>
          </div>
          <div className="sd-uni-grid">
            {universities.map((uni) => (
              <Link key={uni.slug} to={`/university/${uni.slug}`} className="sd-uni-card">
                <div className="sd-uni-card-header">
                  <span className="sd-uni-flag">{uni.flag}</span>
                  <span className="sd-uni-rank">{uni.qsRank}</span>
                </div>
                <h3 className="sd-uni-name">{uni.name}</h3>
                <p className="sd-uni-city">📍 {uni.city} · Est. {uni.founded}</p>
                <p className="sd-uni-overview">{uni.overview.substring(0, 110)}…</p>
                <div className="sd-uni-meta">
                  <span>IELTS {uni.ielts}</span>
                  <span>{uni.intakes[0]}</span>
                </div>
                <span className="sd-uni-link">View University →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sd-cta">
        <div className="sd-container">
          <div className="sd-cta-inner">
            <span className="sd-cta-flag">{data.flag}</span>
            <h2 className="sd-cta-h2">
              Ready to study in {data.name}?<br />
              <em>Let's build your roadmap.</em>
            </h2>
            <p className="sd-cta-sub">
              Free profile evaluation — no commitment. Our counsellors have placed {data.students} students in {data.name} already.
            </p>
            <div className="sd-cta-actions">
              <Link to="/book-slot" className="sd-btn-primary sd-btn-primary--lg">Book Free Counselling</Link>
              <a href="https://wa.me/919110714332" target="_blank" rel="noopener noreferrer" className="sd-btn-wa">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.878-1.42C8.34 21.47 10.13 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                </svg>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}